/**
 * LRU (Least Recently Used) Cache
 *
 * A simple, efficient cache with automatic eviction of oldest items.
 * Useful for caching expensive calculations like bubble positions.
 *
 * Phase 3.1: Performance optimization
 */

export class LRUCache<K, V> {
  private cache = new Map<K, V>()
  private maxSize: number

  constructor(maxSize: number) {
    this.maxSize = maxSize
  }

  /**
   * Get a value from the cache
   * Moves the key to the end (most recently used)
   */
  get(key: K): V | undefined {
    const value = this.cache.get(key)
    if (value !== undefined) {
      // Move to end (most recent)
      this.cache.delete(key)
      this.cache.set(key, value)
    }
    return value
  }

  /**
   * Set a value in the cache
   * Evicts oldest item if cache is full
   */
  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      // Update existing
      this.cache.delete(key)
    } else if (this.cache.size >= this.maxSize) {
      // Evict oldest (first item)
      const firstKey = this.cache.keys().next().value
      if (firstKey) {
        this.cache.delete(firstKey)
      }
    }
    this.cache.set(key, value)
  }

  /**
   * Check if key exists in cache
   */
  has(key: K): boolean {
    return this.cache.has(key)
  }

  /**
   * Delete a key from the cache
   */
  delete(key: K): boolean {
    return this.cache.delete(key)
  }

  /**
   * Clear all items from the cache
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Get the current size of the cache
   */
  get size(): number {
    return this.cache.size
  }

  /**
   * Get all keys in the cache
   */
  keys(): IterableIterator<K> {
    return this.cache.keys()
  }

  /**
   * Get all values in the cache
   */
  values(): IterableIterator<V> {
    return this.cache.values()
  }

  /**
   * Iterate over entries
   */
  entries(): IterableIterator<[K, V]> {
    return this.cache.entries()
  }

  /**
   * Execute a function for each entry
   */
  forEach(callback: (value: V, key: K, map: Map<K, V>) => void): void {
    this.cache.forEach(callback)
  }
}

/**
 * Simple cache with TTL (Time To Live)
 *
 * Entries expire after a specified duration
 */
export class TTLCache<K, V> {
  private cache = new Map<K, { value: V; expires: number }>()
  private ttl: number

  constructor(ttl: number) {
    this.ttl = ttl
  }

  /**
   * Get a value from the cache
   * Returns undefined if expired or not found
   */
  get(key: K): V | undefined {
    const entry = this.cache.get(key)
    if (!entry) return undefined

    if (Date.now() > entry.expires) {
      this.cache.delete(key)
      return undefined
    }

    return entry.value
  }

  /**
   * Set a value with TTL
   */
  set(key: K, value: V): void {
    this.cache.set(key, {
      value,
      expires: Date.now() + this.ttl,
    })
  }

  /**
   * Clear expired entries (cleanup)
   */
  cleanup(): void {
    const now = Date.now()
    this.cache.forEach((entry, key) => {
      if (now > entry.expires) {
        this.cache.delete(key)
      }
    })
  }

  /**
   * Clear all entries
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Get current size
   */
  get size(): number {
    return this.cache.size
  }
}

/**
 * Memoize function with LRU cache
 *
 * Automatically caches function results based on arguments
 */
export function memoizeLRU<T extends (...args: any[]) => any>(
  fn: T,
  maxSize = 500,
  keyGenerator?: (...args: Parameters<T>) => string
): T {
  const cache = new LRUCache<string, ReturnType<T>>(maxSize)

  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = keyGenerator ? keyGenerator(...args) : JSON.stringify(args)

    const cached = cache.get(key)
    if (cached !== undefined) {
      return cached
    }

    const result = fn(...args)
    cache.set(key, result)
    return result
  }) as T
}
